import {
  AssetsAssetIdGetRequest,
  AssetsGetRequest,
  ChannelsChannelIdGetRequest,
  ChannelsChannelIdScheduleEntryIdGetRequest,
  ChannelsChannelIdScheduleGetRequest,
  ChannelsGetRequest,
  LibrariesGetRequest,
  LibrariesLibraryIdGetRequest,
  LibrariesLibraryIdMediaGetRequest,
  LibrariesLibraryIdMediaMediaIdGetRequest,
  PlatformsGetRequest,
  PlatformsPlatformIdGetRequest,
} from '@nativewaves/nw-cloud-sdk/media'
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'

import { MediaAPI } from 'services/api'

const baseIdentifier = 'media'

export const mediaLibraryQueryKeys = {
  base: () => [baseIdentifier, 'media-library'] as const,
  lists: () => [...mediaLibraryQueryKeys.base(), 'list'] as const,
  list: (params: LibrariesGetRequest) =>
    infiniteQueryOptions({
      queryKey: [...mediaLibraryQueryKeys.lists(), params] as const,
      queryFn: ({ pageParam, signal }) =>
        MediaAPI.mediaLibraries.librariesGet({ ...params, next: pageParam }, { signal }),
      initialPageParam: params.next,
      getNextPageParam: (lastPage) => (lastPage.next.length ? lastPage.next : null),
    }),
  details: () => [...mediaLibraryQueryKeys.base(), 'detail'] as const,
  detail: (params: LibrariesLibraryIdGetRequest) =>
    queryOptions({
      queryKey: [...mediaLibraryQueryKeys.details(), params] as const,
      queryFn: () => MediaAPI.mediaLibraries.librariesLibraryIdGet(params),
    }),
  mediaLists: () => [...mediaLibraryQueryKeys.base(), 'media', 'list'] as const,
  mediaList: (params: LibrariesLibraryIdMediaGetRequest) =>
    infiniteQueryOptions({
      queryKey: [...mediaLibraryQueryKeys.lists(), params] as const,
      queryFn: ({ pageParam, signal }) =>
        MediaAPI.mediaLibraries.librariesLibraryIdMediaGet({ ...params, next: pageParam }, { signal }),
      initialPageParam: params.next,
      getNextPageParam: (lastPage) => (lastPage.next.length ? lastPage.next : null),
    }),
  mediaDetails: () => [...mediaLibraryQueryKeys.base(), 'media', 'detail'] as const,
  mediaDetail: (params: LibrariesLibraryIdMediaMediaIdGetRequest) =>
    queryOptions({
      queryKey: [...mediaLibraryQueryKeys.details(), params] as const,
      queryFn: () => MediaAPI.mediaLibraries.librariesLibraryIdMediaMediaIdGet(params),
    }),
}

export const mediaChannelQueryKeys = {
  base: () => [baseIdentifier, 'media-channel'] as const,
  lists: () => [...mediaChannelQueryKeys.base(), 'list'] as const,
  list: (params: ChannelsGetRequest) =>
    infiniteQueryOptions({
      queryKey: [...mediaChannelQueryKeys.lists(), params] as const,
      queryFn: ({ pageParam, signal }) =>
        MediaAPI.mediaChannels.channelsGet({ ...params, next: pageParam }, { signal }),
      initialPageParam: params.next,
      getNextPageParam: (lastPage) => (lastPage.next.length ? lastPage.next : null),
    }),
  details: () => [...mediaChannelQueryKeys.base(), 'detail'] as const,
  detail: (params: ChannelsChannelIdGetRequest) =>
    queryOptions({
      queryKey: [...mediaChannelQueryKeys.details(), params] as const,
      queryFn: () => MediaAPI.mediaChannels.channelsChannelIdGet(params),
    }),
  entryLists: () => [...mediaChannelQueryKeys.base(), 'entry', 'list'] as const,
  entryList: (params: ChannelsChannelIdScheduleGetRequest) =>
    infiniteQueryOptions({
      queryKey: [...mediaChannelQueryKeys.entryLists(), params] as const,
      queryFn: ({ pageParam, signal }) =>
        MediaAPI.mediaChannels.channelsChannelIdScheduleGet({ ...params, next: pageParam }, { signal }),
      initialPageParam: params.next,
      getNextPageParam: (lastPage) => (lastPage.next.length ? lastPage.next : null),
    }),
  entryDetails: () => [...mediaChannelQueryKeys.base(), 'entry', 'detail'] as const,
  entryDetail: (params: ChannelsChannelIdScheduleEntryIdGetRequest) =>
    queryOptions({
      queryKey: [...mediaChannelQueryKeys.entryDetails(), params] as const,
      queryFn: () => MediaAPI.mediaChannels.channelsChannelIdScheduleEntryIdGet(params),
    }),
}

export const mediaPlatformQueryKeys = {
  base: () => [baseIdentifier, 'media-platform'] as const,
  lists: () => [...mediaPlatformQueryKeys.base(), 'list'] as const,
  list: (params: PlatformsGetRequest) =>
    infiniteQueryOptions({
      queryKey: [...mediaPlatformQueryKeys.lists(), params] as const,
      queryFn: ({ pageParam, signal }) =>
        MediaAPI.mediaPlatforms.platformsGet({ ...params, next: pageParam }, { signal }),
      initialPageParam: params.next,
      getNextPageParam: (lastPage) => (lastPage.next.length ? lastPage.next : null),
    }),
  details: () => [...mediaPlatformQueryKeys.base(), 'detail'] as const,
  detail: (params: PlatformsPlatformIdGetRequest) =>
    queryOptions({
      queryKey: [...mediaPlatformQueryKeys.details(), params] as const,
      queryFn: () => MediaAPI.mediaPlatforms.platformsPlatformIdGet(params),
    }),
}

export const mediaAssetQueryKeys = {
  base: () => [baseIdentifier, 'media-asset'] as const,
  lists: () => [...mediaAssetQueryKeys.base(), 'list'] as const,
  list: (params: AssetsGetRequest) =>
    infiniteQueryOptions({
      queryKey: [...mediaAssetQueryKeys.lists(), params] as const,
      queryFn: ({ pageParam, signal }) => MediaAPI.mediaAssets.assetsGet({ ...params, next: pageParam }, { signal }),
      initialPageParam: params.next,
      getNextPageParam: (lastPage) => (lastPage.next.length ? lastPage.next : null),
    }),
  details: () => [...mediaAssetQueryKeys.base(), 'detail'] as const,
  detail: (params: AssetsAssetIdGetRequest) =>
    queryOptions({
      queryKey: [...mediaAssetQueryKeys.details(), params] as const,
      queryFn: () => MediaAPI.mediaAssets.assetsAssetIdGet(params),
    }),
  eventLists: () => [...mediaAssetQueryKeys.base(), 'event-lists'] as const,
  eventList: (params: AssetsGetRequest) =>
    infiniteQueryOptions({
      queryKey: [...mediaPlatformQueryKeys.lists(), params] as const,
      queryFn: ({ pageParam, signal }) =>
        MediaAPI.mediaAssets.assetsGet({ ...params, type: 'events', next: pageParam }, { signal }),
      initialPageParam: params.next,
      getNextPageParam: (lastPage) => (lastPage.next.length ? lastPage.next : null),
    }),
  pageLists: () => [...mediaAssetQueryKeys.base(), 'page-lists'] as const,
  pageList: (params: AssetsGetRequest) =>
    infiniteQueryOptions({
      queryKey: [...mediaPlatformQueryKeys.lists(), params] as const,
      queryFn: ({ pageParam, signal }) =>
        MediaAPI.mediaAssets.assetsGet({ ...params, type: 'pages', next: pageParam }, { signal }),
      initialPageParam: params.next,
      getNextPageParam: (lastPage) => (lastPage.next.length ? lastPage.next : null),
    }),
}
